import PropTypes from "prop-types"
import React, {FC, useEffect, useState} from "react"

import tracking from "@irati-tracking/perks/perks"
import redemptionFlow, {Details} from "@irati/checkout/utils/redemption"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import {Deal} from "@irati/types"
import {Col} from "@irati/ui/src/ColumnRow"
import {DESKTOP, TABLET} from "@irati/ui/src/global/media"
import {Hr} from "@irati/ui/src/Layout"
import {H3, H4, H5} from "@irati/ui/src/Typography"
import history from "@irati/utils/history"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import ReadMore from "@irati-web/perks/src/components/ReadMore"
import {Image} from "../components/Image"
import {RedeemSection} from "../components/RedeemSection"
import ShippingInfo from "../components/ShippingInfo"
import {ContentContainer, ImageContainer, InfoContainer, StyledSpecialOfferLabel} from "./styled"

interface Props {
  deal: Required<Deal>
}

const IMG_SIZES = [
  {
    width: 768,
    height: 768
  },
  {
    width: 960,
    height: 960
  }
]

const MEDIA_QUERIES = [`(max-width: ${TABLET}px) 90vw`, `${DESKTOP}px`]

const MerchandiseInfo: FC<Props> = ({deal}, context) => {
  const [load, setLoad] = useState<Promise<Details> | null>()
  const [error, setError] = useState<Error | null>()
  const {cardImage, header, productConfig, description, brandName} = deal
  const [{salePrice}] = productConfig.products

  useEffect(() => {
    tracking.viewPerk(deal)
  }, [])

  const redeemMerch = async () => {
    tracking.startRedeemPerk(deal)
    const loading = redemptionFlow.dealInfo(deal)

    setError(null)
    setLoad(loading)

    try {
      const details = await loading
      const url = detailsToWebAddress(details)

      history.push(url, context)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoad(null)
    }
  }

  return (
    <InfoContainer padding={[3, 2, 3, 2]}>
      <Col sm={6}>
        <ImageContainer maxWidth="531px" margin="0 auto">
          <Image src={cardImage} imgSizes={IMG_SIZES} mediaQueries={MEDIA_QUERIES} loadingType="eager" alt={header} />
        </ImageContainer>
      </Col>

      <Col sm={6}>
        <ContentContainer maxWidth="515px" margin="0 auto" direction="column" justifyContent="center">
          <Col xsHidden>
            <H5>{brandName}</H5>
          </Col>
          <H3 marginTop={2} marginBottom={2}>
            {header}
          </H3>

          <Col smHidden mdHidden lgHidden>
            <H4 weight="bold">
              <FormattedCurrency isCents={false} value={salePrice?.value} currency={salePrice?.currency} />
            </H4>
          </Col>

          <StyledSpecialOfferLabel />

          <Col xsHidden>
            <RedeemSection onClick={redeemMerch} salePrice={salePrice!} load={load} error={error} />
          </Col>

          <H4 marginTop={4} marginBottom={2}>
            <FormattedMessage id="flexiRewards.merchandise.info.productDetails" />
          </H4>

          <ReadMore maxHeightPx={110}>{description}</ReadMore>

          <Hr padding={[1, 0]} />

          <ShippingInfo />

          <Col smHidden mdHidden lgHidden marginTop={8}>
            <RedeemSection onClick={redeemMerch} load={load} error={error} />
          </Col>
        </ContentContainer>
      </Col>
    </InfoContainer>
  )
}

MerchandiseInfo.contextTypes = {
  appPath: PropTypes.string
}

export default MerchandiseInfo
